import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useGalerias } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Galeria } from "../types/cms";

/**
 * Listado público de galerías: /galeria
 *
 * Muestra todas las galerías publicadas, ordenadas por fecha descendente
 * (las más recientes arriba). Cada tarjeta es un enlace a /galeria/:slug
 * con la imagen de portada y un resumen.
 *
 * Si una galería no tiene `portada` explícita usamos la primera de `fotos`
 * como respaldo. Si no hay ninguna foto en absoluto, renderizamos un
 * bloque neutro con el título — esto solo debería darse en borrador.
 */
export function GaleriasPage() {
  const { data: galerias, isLoading } = useGalerias(48);

  return (
    <>
      <Helmet>
        <title>Galerías — Campus EMJC</title>
        <meta
          name="description"
          content="Imágenes de los campamentos, ferias y actividades de la Escuela Municipal de Jóvenes Científicos de Extremadura."
        />
      </Helmet>

      <section className="container-emjc py-20">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
          Galerías
        </p>
        <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
          Cómo se ve la ciencia desde dentro.
        </h1>
        <p className="mt-6 max-w-3xl text-lg text-gray-700">
          Una selección de imágenes de los campamentos, ferias y excursiones
          que hemos organizado a lo largo de los años. Las fotografías sólo se
          publican cuando contamos con consentimiento firmado de las familias.
        </p>

        {isLoading && (
          <p className="mt-12 text-gray-700">Cargando galerías…</p>
        )}

        {!isLoading && (!galerias || galerias.length === 0) && (
          <div className="mt-12 rounded-2xl border border-gray-300 bg-white p-8">
            <p className="font-display text-xl font-semibold">
              Aún no hay galerías publicadas.
            </p>
            <p className="mt-3 text-gray-700">
              En cuanto subamos las fotos de la próxima edición las verás
              aquí.
            </p>
          </div>
        )}

        {galerias && galerias.length > 0 && (
          <ul className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {galerias.map((g) => (
              <li key={g.documentId ?? g.id}>
                <GaleriaCard galeria={g} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function GaleriaCard({ galeria }: { galeria: Galeria }) {
  const portadaSrc =
    mediaUrl(galeria.portada?.url) ??
    mediaUrl(galeria.fotos?.[0]?.url);

  const numFotos = galeria.fotos?.length ?? 0;
  const fechaTxt = galeria.fecha
    ? new Date(galeria.fecha).toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Link
      to={`/galeria/${galeria.slug}`}
      className="group block overflow-hidden rounded-2xl border border-gray-300 bg-white transition hover:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
    >
      <div className="aspect-[4/3] w-full overflow-hidden bg-brand-paper">
        {portadaSrc ? (
          <img
            src={portadaSrc}
            alt={galeria.portada?.alternativeText ?? galeria.titulo}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-brand-orange-soft">
            <span className="font-display text-xl font-semibold text-brand-orange-deep">
              {galeria.titulo}
            </span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h2 className="font-display text-xl font-semibold leading-tight">
          {galeria.titulo}
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-widest text-gray-700">
          {fechaTxt && <span>{fechaTxt}</span>}
          {numFotos > 0 && (
            <span aria-label={`${numFotos} fotografías`}>
              {numFotos} {numFotos === 1 ? "foto" : "fotos"}
            </span>
          )}
        </div>
        {galeria.descripcion && (
          <p className="mt-3 line-clamp-3 text-sm text-gray-700">
            {galeria.descripcion}
          </p>
        )}
        <p className="mt-4 text-sm font-semibold text-brand-orange-deep">
          Ver galería →
        </p>
      </div>
    </Link>
  );
}
